import { useEffect, useRef, useState, type FormEvent } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { PageShell, Section } from '~/components/layout';
import {
  BusinessFormField,
  BusinessFormSuccess,
  Button,
  ButtonLink,
  Grid,
  Icon,
  SmartImage,
} from '~/components/ui';
import { businessClosing, businessHowItWorksHref } from '~/content/business';
import { EMAIL_PATTERN, isValidPhoneNumber } from '~/lib/business-waitlist-validation';
import { pageHeadMeta } from '~/lib/seo';

export const Route = createFileRoute('/business_/waitlist')({
  head: () =>
    pageHeadMeta({
      title: 'Business Account Waitlist',
      description:
        'We’re rebuilding nsave Business, so new general business accounts are paused until the end of the year. Join the waitlist to be first in line when doors reopen.',
      path: '/business/waitlist',
    }),
  component: BusinessWaitlistPage,
});

type SubmissionState =
  | { status: 'idle' | 'submitting'; message?: never }
  // A success restored from storage has no email: only this visitor's own
  // submission is allowed to display an address.
  | { status: 'success'; email?: string }
  | { status: 'error'; message: string };

type WaitlistFieldName =
  | 'businessName'
  | 'website'
  | 'contactEmail'
  | 'contactMobile'
  | 'employeeCount'
  | 'employeeCountries'
  | 'monthlyPayrollVolume';

type FieldErrors = Partial<Record<WaitlistFieldName, string>>;

const WAITLIST_FIELDS: WaitlistFieldName[] = [
  'businessName',
  'website',
  'contactEmail',
  'contactMobile',
  'employeeCount',
  'employeeCountries',
  'monthlyPayrollVolume',
];

// Stores only when this browser last joined, never the applicant's details.
const WAITLIST_SUCCESS_STORAGE_KEY = 'nsave-business-waitlist-joined-at';
const WAITLIST_SUCCESS_TTL_MS = 30 * 24 * 60 * 60 * 1_000;

function sanitizePhoneNumber(value: string): string {
  const sanitized = value.replace(/[^0-9+().\-\s]/g, '');
  return sanitized.replace(/\+/g, (character, index) => (index === 0 ? character : ''));
}

function hasRecentlyJoinedWaitlist(): boolean {
  try {
    // Older builds stored the raw email under this key; clear it.
    window.localStorage.removeItem('nsave-business-waitlist-email');
    const joinedAt = Number(window.localStorage.getItem(WAITLIST_SUCCESS_STORAGE_KEY));
    if (joinedAt > 0 && Date.now() - joinedAt < WAITLIST_SUCCESS_TTL_MS) return true;
    window.localStorage.removeItem(WAITLIST_SUCCESS_STORAGE_KEY);
    return false;
  } catch {
    return false;
  }
}

function rememberWaitlistJoined() {
  try {
    window.localStorage.setItem(WAITLIST_SUCCESS_STORAGE_KEY, String(Date.now()));
  } catch {
    // The success state still works when storage is unavailable.
  }
}

function validateWaitlistForm(form: FormData): FieldErrors {
  const value = (name: WaitlistFieldName) => String(form.get(name) ?? '').trim();
  const errors: FieldErrors = {};
  const businessName = value('businessName');
  const website = value('website');
  const email = value('contactEmail');
  const mobile = value('contactMobile');
  const employees = Number(value('employeeCount'));
  const countries = value('employeeCountries');
  const payrollVolume = value('monthlyPayrollVolume');

  if (businessName.length < 2) errors.businessName = 'Enter your business name.';
  try {
    const websiteUrl = new URL(website);
    if (!['http:', 'https:'].includes(websiteUrl.protocol)) throw new Error('Invalid protocol');
  } catch {
    errors.website = 'Enter a full website URL, including https://.';
  }
  if (!EMAIL_PATTERN.test(email)) errors.contactEmail = 'Enter a valid work email address.';
  if (!isValidPhoneNumber(mobile)) errors.contactMobile = 'Enter a valid mobile number.';
  if (!Number.isInteger(employees) || employees < 1 || employees > 1_000_000) {
    errors.employeeCount = 'Enter a number between 1 and 1,000,000.';
  }
  if (countries.length < 2) errors.employeeCountries = 'Tell us where your employees are based.';
  if (!payrollVolume) errors.monthlyPayrollVolume = 'Enter your expected monthly payroll volume.';
  return errors;
}

function BusinessWaitlistPage() {
  const [submission, setSubmission] = useState<SubmissionState>({ status: 'idle' });
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  // performance.now() is monotonic, so the elapsed time sent to the server is
  // unaffected by a wrong device clock.
  const formStartedAt = useRef(performance.now());

  useEffect(() => {
    if (hasRecentlyJoinedWaitlist()) setSubmission({ status: 'success' });
  }, []);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const errors = validateWaitlistForm(form);
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      setSubmission({ status: 'error', message: 'Check the highlighted fields and try again.' });
      const firstInvalidField = WAITLIST_FIELDS.find((field) => errors[field]);
      if (firstInvalidField) {
        (formElement.elements.namedItem(firstInvalidField) as HTMLElement | null)?.focus();
      }
      return;
    }

    setFieldErrors({});
    setSubmission({ status: 'submitting' });
    const contactEmail = String(form.get('contactEmail') ?? '').trim();

    try {
      const response = await fetch('/api/business-waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          businessName: form.get('businessName'),
          website: form.get('website'),
          contactEmail: form.get('contactEmail'),
          contactMobile: form.get('contactMobile'),
          employeeCount: form.get('employeeCount'),
          employeeCountries: form.get('employeeCountries'),
          monthlyPayrollVolume: form.get('monthlyPayrollVolume'),
          fax: form.get('fax'),
          elapsedMs: Math.round(performance.now() - formStartedAt.current),
        }),
      });
      const result = (await response.json().catch(() => null)) as {
        ok?: boolean;
        code?: string;
      } | null;

      if (!response.ok || !result?.ok) {
        setSubmission({
          status: 'error',
          message:
            result?.code === 'invalid_submission'
              ? 'Check the highlighted fields and try again.'
              : result?.code === 'rate_limited'
                ? 'Too many attempts. Please wait 15 minutes and try again.'
                : 'We could not submit your details right now. Please try again.',
        });
        return;
      }

      formElement.reset();
      rememberWaitlistJoined();
      setSubmission({ status: 'success', email: contactEmail });
    } catch {
      setSubmission({
        status: 'error',
        message: 'We could not submit your details right now. Please try again.',
      });
    }
  };

  const onInput = (event: FormEvent<HTMLFormElement>) => {
    const field = (event.target as HTMLInputElement | HTMLTextAreaElement).name;
    if (WAITLIST_FIELDS.includes(field as WaitlistFieldName)) {
      setFieldErrors((current) => {
        if (!current[field as WaitlistFieldName]) return current;
        const next = { ...current };
        delete next[field as WaitlistFieldName];
        return next;
      });
    }
    if (submission.status === 'error') setSubmission({ status: 'idle' });
  };

  return (
    <PageShell footerClassName="[&>div]:!pt-12 md:[&>div]:!pt-16">
      <Section className="pt-16 md:pt-24">
        <Grid className="items-start gap-y-12">
          <div className="col-span-9 lg:col-span-4 lg:pr-10 lg:pt-5">
            <h1 className="max-w-2xl text-[40px] leading-[1.08] font-semibold tracking-[-0.03em] text-balance md:text-[60px]">
              We’re making nsave Business better
            </h1>
            <div className="mt-9 max-w-xl space-y-8 text-body leading-relaxed text-grey-900">
              <p>
                We’re rebuilding nsave Business from the ground up, so we’ve paused new general
                accounts until the end of the year.
              </p>
              <p>Join the waitlist and you’ll be first in line when doors reopen.</p>
              <p>
                <strong className="font-semibold text-ink">
                  Got an international team to pay?
                </strong>{' '}
                You don’t have to wait. Payroll is open now, with instant salaries and zero nsave
                transfer fees.{' '}
                <Link
                  to={businessHowItWorksHref}
                  className="font-semibold text-ink underline underline-offset-4 hover:text-red-500">
                  See how nsave Payroll works →
                </Link>
              </p>
            </div>
          </div>

          <div className="col-span-9 rounded-[28px] border border-black-6 bg-grey-50 p-7 md:p-9 lg:col-span-5 lg:p-10">
            {submission.status === 'success' ? (
              <BusinessFormSuccess title="That’s it.">
                You’re on the waitlist. We’ll email{' '}
                {submission.email ? (
                  <span className="font-medium text-ink">{submission.email}</span>
                ) : (
                  'you'
                )}{' '}
                when business account applications reopen.
              </BusinessFormSuccess>
            ) : (
              <>
                <div className="space-y-4">
                  <h2 className="text-[24px] leading-[1.2] font-semibold tracking-[-0.025em] text-ink md:text-[26px]">
                    Join the waitlist
                  </h2>
                  <p className="max-w-xl text-body leading-[1.5] text-grey-700">
                    Tell us about your business and payroll needs. We’ll contact you when
                    applications reopen.
                  </p>
                </div>
                <form
                  className="mt-8 grid gap-x-5 gap-y-5 md:grid-cols-2"
                  onSubmit={onSubmit}
                  onInput={onInput}
                  noValidate>
                  <BusinessFormField
                    id="business-waitlist-name"
                    name="businessName"
                    label="Business name"
                    autoComplete="organization"
                    placeholder="Acme Inc."
                    minLength={2}
                    maxLength={200}
                    error={fieldErrors.businessName}
                  />
                  <BusinessFormField
                    id="business-waitlist-website"
                    name="website"
                    type="url"
                    label="Website"
                    autoComplete="url"
                    placeholder="https://company.com"
                    maxLength={500}
                    error={fieldErrors.website}
                  />
                  <BusinessFormField
                    id="business-waitlist-email"
                    name="contactEmail"
                    type="email"
                    label="Contact email"
                    autoComplete="email"
                    placeholder="you@company.com"
                    maxLength={254}
                    error={fieldErrors.contactEmail}
                  />
                  <BusinessFormField
                    id="business-waitlist-mobile"
                    name="contactMobile"
                    type="tel"
                    label="Contact mobile number"
                    autoComplete="tel"
                    inputMode="tel"
                    placeholder="+44 20 1234 5678"
                    maxLength={30}
                    onInput={(event) => {
                      event.currentTarget.value = sanitizePhoneNumber(event.currentTarget.value);
                    }}
                    error={fieldErrors.contactMobile}
                  />
                  <BusinessFormField
                    id="business-waitlist-employees"
                    name="employeeCount"
                    type="number"
                    label="Number of employees"
                    min="1"
                    max="1000000"
                    inputMode="numeric"
                    placeholder="25"
                    error={fieldErrors.employeeCount}
                  />
                  <BusinessFormField
                    id="business-waitlist-volume"
                    name="monthlyPayrollVolume"
                    label="Expected payroll volume per month"
                    placeholder="$50,000 USD"
                    maxLength={100}
                    error={fieldErrors.monthlyPayrollVolume}
                  />
                  <BusinessFormField
                    id="business-waitlist-countries"
                    name="employeeCountries"
                    label="Countries of employees"
                    placeholder="Egypt, Pakistan, Bangladesh"
                    maxLength={1000}
                    error={fieldErrors.employeeCountries}
                    hint="Separate multiple countries with commas."
                    className="md:col-span-2"
                  />
                  <input
                    type="text"
                    name="fax"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    className="absolute -left-[10000px] h-px w-px overflow-hidden"
                  />
                  {submission.status === 'error' && (
                    <div
                      role="alert"
                      className="rounded-[16px] border border-red-500/20 bg-red-500/5 px-4 py-3 text-body-s text-red-500 md:col-span-2">
                      {submission.message}
                    </div>
                  )}
                  <Button
                    type="submit"
                    className="mt-1 min-h-14 w-full md:col-span-2"
                    disabled={submission.status === 'submitting'}>
                    {submission.status === 'submitting' ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="size-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                        Submitting…
                      </span>
                    ) : (
                      'Join the waitlist'
                    )}
                  </Button>
                  <p className="text-[13px] leading-[1.55] text-grey-600 md:col-span-2">
                    By joining, you agree to receive business product updates. See our{' '}
                    <Link
                      to="/privacy-policy"
                      className="font-medium text-ink underline underline-offset-4">
                      privacy policy
                    </Link>
                    .
                  </p>
                </form>
              </>
            )}
          </div>
        </Grid>
      </Section>

      <Section className="!pt-6 !pb-0 md:!pt-10 md:!pb-0">
        <div className="border-y border-black-6 py-8 md:py-10 lg:grid lg:grid-cols-9 lg:items-center lg:gap-5">
          <div className="lg:col-span-3">
            <h2 className="text-h4 leading-[1.15] font-semibold tracking-[-0.025em] text-balance md:text-h3">
              Built for more than
              <br />
              just payroll.
            </h2>
          </div>
          <div className="mt-8 grid gap-6 sm:grid-cols-3 lg:col-span-6 lg:mt-0">
            {[
              { icon: 'download' as const, label: 'Receive client payments' },
              { icon: 'coin' as const, label: 'Pay suppliers' },
              { icon: 'card' as const, label: 'Manage business expenses' },
            ].map((item, index) => (
              <div
                key={item.label}
                className={`flex items-center gap-4 sm:flex-col sm:items-start sm:gap-5 sm:px-5 ${index > 0 ? 'sm:border-l sm:border-black-6' : ''}`}>
                <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-grey-50 text-ink">
                  <Icon name={item.icon} className="size-5" />
                </span>
                <p className="text-body-m font-semibold text-ink">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      <Section className="!pt-6 !pb-0 md:!pt-10 md:!pb-0">
        <div className="relative min-h-[460px] overflow-hidden md:min-h-[520px]">
          <SmartImage image={businessClosing.image} className="absolute inset-0 size-full" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/45 to-transparent rtl:bg-gradient-to-l" />
          <div className="relative z-10 flex min-h-[460px] max-w-2xl flex-col justify-end p-7 text-white md:min-h-[520px] md:p-12 lg:p-14">
            <h2 className="text-h3 font-semibold tracking-[-0.03em] text-balance md:text-h2">
              {businessClosing.title}
            </h2>
            <p className="mt-5 max-w-xl text-body text-white/85">{businessClosing.body}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <ButtonLink
                href={businessClosing.primaryCta.href}
                external={businessClosing.primaryCta.external}
                size="md">
                {businessClosing.primaryCta.label}
              </ButtonLink>
              <ButtonLink
                href={businessClosing.secondaryCta.href}
                external={businessClosing.secondaryCta.external}
                variant="outline"
                size="md"
                className="border-white bg-white text-ink hover:bg-grey-50">
                {businessClosing.secondaryCta.label}
              </ButtonLink>
            </div>
          </div>
        </div>
      </Section>
    </PageShell>
  );
}
